import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaEye, FaEyeSlash, FaSpinner } from 'react-icons/fa';
import { useCloudflareAuth } from '../../contexts/CloudflareAuthContext';
import GoogleLoginButton from '../../components/GoogleLoginButton';
import { formatTextField, formatMobilePhone, getPhoneDigits } from '../../utils/formHelpers';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useCloudflareAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<{ type: 'error' | 'success', message: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    const value = formatTextField(name, e.target.value);
    if (e.target instanceof HTMLInputElement) e.target.value = value;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // The +91 prefix is shown statically next to the input, so the field holds only the 10 digits
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = getPhoneDigits(e.target.value);
    if (e.target instanceof HTMLInputElement) e.target.value = digits;
    setFormData(prev => ({ ...prev, phone: digits }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationMessage(null);

    try {
      // Validate form data
      if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
        setValidationMessage({ type: 'error', message: 'Please fill in all fields' });
        setIsLoading(false);
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setValidationMessage({ type: 'error', message: 'Passwords do not match' });
        setIsLoading(false);
        return;
      }

      // Validate phone number format only if provided
      if (formData.phone) {
        const digits = formData.phone.replace(/\D/g, '');
        const trimmed = digits.length > 10 && digits.startsWith('91') ? digits.slice(2) : digits;
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(trimmed)) {
          setValidationMessage({ type: 'error', message: 'Please enter a valid 10-digit mobile number' });
          setIsLoading(false);
          return;
        }
      }

      // Register user via Cloudflare API
      const success = await register(
        formData.email,
        formData.password,
        formData.fullName,
        formData.phone ? formatMobilePhone(formData.phone) : undefined
      );

      if (success) {
        setValidationMessage({ type: 'success', message: 'Registration successful! Please login to continue.' });
        navigate('/login');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setValidationMessage({ type: 'error', message: error.message || 'Registration failed' });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 -mt-12 p-2">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900/50 via-slate-900/50 to-slate-900/90" />
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute w-screen h-screen">
          <div className="absolute top-0 left-0 w-[200%] h-[200%] origin-top-left bg-gradient-to-br from-blue-500/5 to-purple-500/5 animate-rotate-slow" />
          <div className="absolute bottom-0 right-0 w-[200%] h-[200%] origin-bottom-right bg-gradient-to-tl from-indigo-500/5 to-pink-500/5 animate-rotate-slow-reverse" />
        </div>
      </div>

      {/* Register Form */}
      <div className="max-w-2xl w-full relative z-10 mx-auto px-4">
        <div className="floating-card glass-effect rounded-2xl p-8 shadow-2xl backdrop-blur-xl bg-slate-900/40">
          {/* Logo Section */}
          <div className="text-center space-y-4 mb-6">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-full blur-2xl opacity-75 animate-pulse" />
              <Link to="/">
                <img
                  src="/images/logo.png"
                  alt="Logo"
                  className="relative mx-auto h-24 w-auto animate-float"
                />
              </Link>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-4xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                Create Account
              </h2>
              <p className="text-dark-text-secondary text-lg">
                Join us in making a safer tomorrow
              </p>
            </div>
          </div>

          {/* Form Section */}
          <form 
            onSubmit={handleSubmit} 
            className="space-y-6" 
            autoComplete="off"
            data-lpignore="true"
          >
            {/* Full Name Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <FaUser className="h-5 w-5 text-gray-500" style={{ filter: 'none' }} />
              </div>
              <input
                type="text"
                name="fullName"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                value={formData.fullName}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-gray-400 placeholder-gray-400 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(156,163,175)]"
                placeholder="Full Name"
              />
            </div>

            {/* Mobile Number and Email */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <FaPhone className="h-5 w-5 text-gray-500" style={{ filter: 'none' }} />
              </div>
              <div className="absolute inset-y-0 left-10 flex items-center">
                <span className="text-gray-500">+91</span>
              </div>
              <input
                type="tel"
                name="phone"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                value={formData.phone}
                onChange={handlePhoneChange}
                className="block w-full pl-20 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-gray-400 placeholder-gray-400 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(156,163,175)]"
                placeholder="Mobile Number"
                inputMode="numeric"
                maxLength={16}
              />
              </div>

              <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <FaEnvelope className="h-5 w-5 text-gray-500" style={{ filter: 'none' }} />
              </div>
              <input
                type="email"
                name="email"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                value={formData.email}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-gray-400 placeholder-gray-400 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(156,163,175)]"
                placeholder="Email address"
              />
              </div>
            </div>

            {/* Password and Confirm Password */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <FaLock className="h-5 w-5 text-gray-500" style={{ filter: 'none' }} />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                value={formData.password}
                onChange={handleChange}
                className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-gray-400 placeholder-gray-400 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(156,163,175)]"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
              >
                {showPassword ? <FaEyeSlash className="h-5 w-5 filter-none" /> : <FaEye className="h-5 w-5 filter-none" />}
              </button>
              </div>

              <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <FaLock className="h-5 w-5 text-gray-500" style={{ filter: 'none' }} />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white/10 backdrop-blur-sm text-gray-400 placeholder-gray-400 [&:-webkit-autofill]:bg-white/5 [&:-webkit-autofill]:[transition-delay:9999s] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(156,163,175)]"
                placeholder="Confirm Password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
              >
                {showConfirmPassword ? <FaEyeSlash className="h-5 w-5 filter-none" /> : <FaEye className="h-5 w-5 filter-none" />}
              </button>
              </div>
            </div>

            {/* Account actions */}
            <div className="space-y-5">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
                <button
                  type="submit"
                  className="relative btn-primary group w-full flex items-center justify-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading && <FaSpinner className="h-5 w-5 animate-spin" />}
                  {isLoading ? 'Creating...' : 'Create Account'}
                </button>
              </div>

              <div className="flex items-center gap-4 py-1">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-xs font-medium uppercase tracking-widest text-gray-500">or</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <GoogleLoginButton />
            </div>

            {/* Login Link */}
            <div className="text-center mt-4">
              <span className="text-gray-400">Already have an account? </span>
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
                Sign in
              </Link>
            </div>
          </form>
        </div>

        {/* Validation Message */}
        {validationMessage && (
          <div
            className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 backdrop-blur-sm ${
              validationMessage.type === 'error'
                ? 'bg-red-950 text-red-100'
                : 'bg-[#064e3b] text-emerald-100'
            }`}
          >
            {validationMessage.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
